import {
  Button,
  Container,
  Flex,
  Box,
  Text,
  VStack,
  useBreakpointValue,
  Image,
  Spinner,
  BoxProps,
} from "@chakra-ui/react";
import engageBlurPlaceholder from "../engage-blur-placeholder.png";
import { useCallback, useEffect, useState } from "react";
import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import { Navigate, Routes, Route, useNavigate } from "react-router-dom";
import Events from "./Events/Events";
import Bundles from "./Bundles/Bundles";
import Team from "./Team";
import Settings from "./Settings/Organization";
import EventDetail from "./EventDetail";
import BundleDetail from "./Bundles/BundleDetail";
import { ModalType } from "../utils";
import FeedManager from "./FeedManager/FeedManager";
import actions from "../actions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import logo from "../darksmall.png";
import ChangeOrganizationModal from "./ChangeOrganizationModal";
import { CognitoUser } from "amazon-cognito-identity-js";
import Detail from "./EventDetail/Nav/Detail";
import GuestlistNavSection from "./EventDetail/Nav/Guestlist/GuestlistNavSection";
import ReportingNavSection from "./EventDetail/Nav/Reporting/ReportingNavSection";
import SettingsNavSection from "./EventDetail/Nav/Settings/SettingsNavSection";
import MarketingNavSection from "./EventDetail/Nav/Marketing/MarketingNavSection";
import AtteendeeNavSection from "./EventDetail/Nav/Attendees/AttendeesNavSection";
import WaitlistNavSection from "./EventDetail/Nav/Waitlist/WaitlistNavSection";
import AnalyticsNavSection from "./EventDetail/Nav/Analytics/AnalyticsNavSection";
import PayoutsNavSection from "./EventDetail/Nav/Payouts/PayoutsNavSection";
import Dashboard from "./Dashboard/Dashboard";
import CampaignsOverview from "./Campaigns/Overview";
import AdsCampaigns from "./Campaigns/Ads";
import CampaignDetail from "./Campaigns/Ads/CampaignDetail";
import CampaignsSettings from "./Campaigns/Settings";
import CreateCampaign from "./Campaigns/CreateCampaign/BuildCampaign";
import ReviewCampaign from "./Campaigns/CreateCampaign/ReviewCampaign";
import Segments from "./Campaigns/Segments";
import Contacts from "./Campaigns/Contacts";
import Campaign from "./Campaigns/Overview/Campaign";
import SegmentDetail from "./Campaigns/Segments/SegmentDetail";
import EngageOverview from "./Engage/Overview";
import PayoutsDashboard from "./Payouts/Dashboard";
import PayoutDetails from "./Payouts/PayoutDetails";
import ContactDetail from "./Campaigns/Contacts/ContactDetail";
import CampaignTemplates from "./Campaigns/Templates";
import ContactsImport from "./Campaigns/Imports";
import ProtectedRoute from "./ProtectedRoute";
import Billing from "./Settings/Billing";
import { useOrganization } from "./EventDetail/hooks/useOrganization";
import BlurOverlay from "./Campaigns/Components/BlurOverlay";
import { FiMail } from "react-icons/fi";
import TicketSalesReportingPage from "./Reporting/TicketSalesReportingPage";

export const PageContainer = ({
  children,
  px = 3,
  py = 3,
  innerProps,
}: {
  children: React.ReactNode;
  px?: number;
  py?: number;
  innerProps?: BoxProps;
}) => (
  <Container
    maxW="100%"
    px={px}
    py={py}
    flex="1"
    height="100vh"
    overflowY="scroll"
    overflowX="hidden"
  >
    <Box
      bg="white"
      py={4}
      px={8}
      borderRadius={8}
      boxShadow="0px 0px 15px 0px rgba(0, 0, 0, 0.10)"
      height="100%"
      overflowY="auto"
      {...innerProps}
    >
      {children}
    </Box>
  </Container>
);

const PageContainerWithoutPadding = ({
  children,
  px = 4,
  py = 8,
}: {
  children: React.ReactNode;
  px?: number;
  py?: number;
}) => (
  <PageContainer
    innerProps={{
      py: 0,
      px: 0,
      borderTopLeftRadius: 8,
      overscrollBehavior: "none",
    }}
  >
    {children}
  </PageContainer>
);

const EventManager = ({
  cognitoUser,
  setModalType,
}: {
  cognitoUser: CognitoUser;
  setModalType: (state: ModalType) => void;
}) => {
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const [currentUser, setCurrentUser] = useState<KYDOrganizationUser>();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Manager";
    setIsLoading(true);
    const checkAccessToken = async (_me: KYDOrganizationUser) => {
      const accessToken = await actions.ADMIN.fetchAccessToken();
      if (accessToken) {
        const payload = accessToken.decodePayload();
        if (
          _me &&
          _me.updated_at &&
          payload["iat"] < new Date(_me.updated_at).getTime() / 1000
        ) {
          await refreshAccessToken();
        }
      }
    };

    const fetchMe = async () => {
      try {
        const me = await actions.ADMIN.fetchMe();
        await checkAccessToken(me);
        setCurrentUser(me);
      } catch (err) {
        //dialog({ text: err.message });
      }

      setIsLoading(false);
    };
    fetchMe();
  }, [cognitoUser]);

  const refreshAccessToken = useCallback(async () => {
    await actions.ADMIN.refreshAccessToken();
  }, []);

  const switchOrganization = useCallback(
    async (organization_id: string) => {
      setIsLoading(true);
      try {
        await actions.ADMIN.updateMe(organization_id);
        await refreshAccessToken();
        window.location.reload();
      } catch (err) {
        setIsLoading(false);
      }
    },
    [refreshAccessToken]
  );

  const {
    organization,
    loading: organizationLoading,
    refetch: refetchOrganization,
    // This is used to check if org has valid plan
    // Probably we need a simpler org fetch that just works for all users
  } = useOrganization({ isOkayToFail: true });
  const hasValidPlan = organization?.subscriptions?.some(
    (sub) => sub.status === "OPEN"
  );

  const hasValidPaymentMethod = Boolean(
    organization?.settings?.default_payment_method?.payment_method_id ||
      organization?.settings?.default_payment_method_id
  );
  const canEvaluateEngageAccess = !organizationLoading && !!organization;

  return currentUser ? (
    <Flex
      as="section"
      direction={{ base: "column", md: "row" }}
      height={{ base: "100dvh", md: "100vh" }}
      w="100%"
      bg="#EAEAEC"
      sx={{
        // CSS fallback for browsers that don't support dvh
        "@supports not (height: 100dvh)": {
          height: { base: "100vh" },
        },
      }}
    >
      <ChangeOrganizationModal
        currentUser={currentUser}
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onSave={switchOrganization}
        isLoading={isLoading}
      />
      {isDesktop ? (
        <Sidebar
          currentUser={currentUser}
          onOrganizationClick={() => setIsOpen(true)}
          scopes={currentUser?.scopes}
        />
      ) : (
        <Navbar
          currentUser={currentUser}
          onOrganizationClick={() => setIsOpen(true)}
          scopes={currentUser?.scopes}
        />
      )}

      <Routes>
        <Route
          path="/settings/organization"
          element={
            <PageContainer>
              <Settings />
            </PageContainer>
          }
        />
        {currentUser?.scopes?.includes("organizations/write") ? (
          <Route
            path="/settings/billing"
            element={
              <PageContainer>
                <Billing
                  permissions={currentUser?.scopes}
                  organization={organization}
                  refetchOrganization={refetchOrganization}
                  isLoadingOrganization={organizationLoading}
                />
              </PageContainer>
            }
          />
        ) : null}
        <Route
          path="/bio"
          element={
            <PageContainer>
              <FeedManager />
            </PageContainer>
          }
        />
        {currentUser?.scopes?.find((s) => s.startsWith("crm/")) ? (
          <>
            <Route
              path="/engage/overview"
              element={
                <PageContainer>
                  <EngageOverview userScopes={currentUser?.scopes} />
                </PageContainer>
              }
            />
            <Route
              path="/campaigns"
              element={
                <PageContainer
                  innerProps={{ p: !hasValidPlan ? 0 : undefined }}
                >
                  <BlurOverlay
                    isBlurred={
                      canEvaluateEngageAccess &&
                      (!hasValidPlan || !hasValidPaymentMethod)
                    }
                    image={engageBlurPlaceholder}
                    // isBlurred={true}
                    modalIcon={FiMail}
                    modalTitle={
                      !hasValidPaymentMethod
                        ? "Add Payment Method"
                        : "Activate Email Marketing"
                    }
                    modalMessage={`Email marketing requires an active subscription.${
                      !hasValidPaymentMethod
                        ? " Add a payment method to continue."
                        : ""
                    }`}
                    ctaText={
                      !hasValidPaymentMethod ? "Add Payment Method" : null
                    }
                    onCtaClick={
                      !hasValidPaymentMethod
                        ? () => {
                            navigate("/settings/billing");
                          }
                        : null
                    }
                  >
                    <CampaignsOverview
                      permissions={currentUser?.scopes}
                      organization={organization}
                    />
                  </BlurOverlay>
                </PageContainer>
              }
            />
            <Route
              path="/campaigns/ads"
              element={
                <PageContainer>
                  <AdsCampaigns />
                </PageContainer>
              }
            />
            <Route
              path="/campaigns/ads/:campaign_id"
              element={
                <PageContainerWithoutPadding>
                  <CampaignDetail permissions={currentUser?.scopes} />
                </PageContainerWithoutPadding>
              }
            />
            <Route
              path="/campaigns/settings"
              element={
                <PageContainer>
                  <CampaignsSettings />
                </PageContainer>
              }
            />
            <Route
              path="/campaigns/create"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/campaigns:write",
                    "crm/campaigns:read",
                    "crm/write",
                    "crm/read",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer innerProps={{ p: 0 }}>
                    <CreateCampaign permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/templates"
              element={
                <ProtectedRoute
                  requiredScopes={["crm/campaigns:write", "crm/write"]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <CampaignTemplates permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/create/review"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/campaigns:write",
                    "crm/campaigns:read",
                    "crm/write",
                    "crm/read",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer innerProps={{ p: 0 }}>
                    <ReviewCampaign permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:id"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/campaigns:read",
                    "crm/campaigns:write",
                    "crm/read",
                    "crm/write",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer innerProps={{ p: 0 }}>
                    <Campaign permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />

            <Route
              path="/campaigns/segments/:id"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/segments:read",
                    "crm/segments:write",
                    "crm/read",
                    "crm/write",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <SegmentDetail permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/segments"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/segments:read",
                    "crm/segments:write",
                    "crm/read",
                    "crm/write",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <Segments permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/contacts"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/contacts:read",
                    "crm/contacts:write",
                    "crm/read",
                    "crm/write",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <Contacts permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/contacts/:id"
              element={
                <ProtectedRoute
                  requiredScopes={[
                    "crm/contacts:read",
                    "crm/contacts:write",
                    "crm/read",
                    "crm/write",
                  ]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <ContactDetail permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/imports"
              element={
                <ProtectedRoute
                  requiredScopes={["crm/imports:read", "crm/read"]}
                  userScopes={currentUser?.scopes}
                >
                  <PageContainer>
                    <ContactsImport permissions={currentUser?.scopes} />
                  </PageContainer>
                </ProtectedRoute>
              }
            />
          </>
        ) : null}
        {currentUser?.scopes?.includes("organizations/write") ||
        currentUser?.scopes?.includes("organizations/read") ||
        currentUser?.scopes?.includes("organizations/users:read") ? (
          <Route
            path="/team"
            element={
              <PageContainer>
                <Team scopes={currentUser?.scopes} currentUser={currentUser} />
              </PageContainer>
            }
          />
        ) : null}
        <Route path="/events">
          <Route
            index
            element={
              <PageContainer>
                <Events />
              </PageContainer>
            }
          />
          <Route
            path=":event_id"
            element={
              <PageContainerWithoutPadding>
                <EventDetail setAppModalType={setModalType} />
              </PageContainerWithoutPadding>
            }
          >
            <Route index element={<Detail />} />
            <Route path="guestlist" element={<GuestlistNavSection />} />
            <Route path="attendees" element={<AtteendeeNavSection />} />
            <Route path="waitlist" element={<WaitlistNavSection />} />
            <Route path="marketing" element={<MarketingNavSection />} />
            <Route path="analytics" element={<AnalyticsNavSection />} />
            <Route path="reporting" element={<ReportingNavSection />} />
            <Route path="settings" element={<SettingsNavSection />} />
            <Route path="payouts" element={<PayoutsNavSection />} />
          </Route>
        </Route>
        {currentUser?.scopes?.includes("organizations/overview:read") ||
        currentUser?.scopes?.includes("organizations/write") ? (
          <Route
            path="/dashboard"
            element={
              <PageContainer>
                <Dashboard />
              </PageContainer>
            }
          />
        ) : null}
        <Route path="/bundles">
          <Route
            index
            element={
              <PageContainer>
                <Bundles currentUser={currentUser} />
              </PageContainer>
            }
          />
          <Route
            path=":bundle_id"
            element={
              <PageContainer>
                <BundleDetail />
              </PageContainer>
            }
          >
            <Route index element={<Detail />} />
          </Route>
        </Route>
        {currentUser?.scopes?.includes("organizations/write") ||
        currentUser?.scopes?.includes("organizations/payouts:write") ||
        currentUser?.scopes?.includes("organizations/payouts:read") ? (
          <>
            <Route
              path="/payouts"
              element={
                <PageContainer>
                  <PayoutsDashboard />
                </PageContainer>
              }
            />
            <Route
              path="/payouts/:payout_id"
              element={
                <PageContainer>
                  <PayoutDetails />
                </PageContainer>
              }
            />
          </>
        ) : null}
        {currentUser?.scopes?.includes("organizations/payouts:read") ||
        currentUser?.scopes?.includes("organizations/read") ? (
          <Route
            path="/reporting"
            element={
              <PageContainer>
                <TicketSalesReportingPage />
              </PageContainer>
            }
          />
        ) : null}

        <Route path="*" element={<Navigate to={"/events"} />} />
      </Routes>
    </Flex>
  ) : (
    <VStack h="100vh" justify={"center"}>
      <Image src={logo} w="100px" />
      {isLoading ? (
        <Spinner color="black" />
      ) : (
        <>
          <Text>Please sign in to access your dashboard</Text>
          <Button
            bg="black"
            color="white"
            onClick={() => setModalType(ModalType.SIGN_IN_PHONE_NUMBER)}
          >
            Sign In
          </Button>
        </>
      )}
    </VStack>
  );
};

export default EventManager;
